import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { ContentSectionHeader } from "../../../../components/common";
import SurveyPageFilters, {
  type SurveyPageFilterHandlers,
  type SurveyPageFilterState,
} from "./SurveyPageFilters";
import type { SurveyListFilters } from "../hooks/useGetSurveyList";
import type { SurveySortKey } from "../types/survey.types";

type SurveyListToolbarProps = {
  search: string;
  setSearch: (value: string) => void;
  filters: SurveyListFilters;
  setFilters: React.Dispatch<React.SetStateAction<SurveyListFilters>>;
  setPage: (page: number) => void;
  setSorting: (sorting: { field: string; direction: "asc" | "desc"; sortKey?: SurveySortKey }) => void;
  isLoading: boolean;
  onRefresh: () => void | Promise<void>;
};

const SurveyListToolbar = ({
  search,
  setSearch,
  filters,
  setFilters,
  setPage,
  setSorting,
  isLoading,
  onRefresh,
}: SurveyListToolbarProps) => {
  const { t } = useTranslation();
  const [timeFilter, setTimeFilter] = useState("All time");
  const [sortFilter, setSortFilter] = useState("newest_first");
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();

  const getRelativeTimeFilter = (value: string) => {
    switch (value) {
      case "Today": return "today";
      case "This Week": return "this_week";
      case "This Month": return "this_month";
      case "Last Year": return "past";
      default: return undefined;
    }
  };

  const handleTimeFilterChange = (value: string) => {
    setTimeFilter(value);
    if (value === "All time") {
      setFilters(({ date_filter: _dateFilter, ...rest }) => rest);
      return;
    }
    if (value === "Custom Range") return;

    const relative = getRelativeTimeFilter(value);
    if (relative) {
      setFilters((previous) => ({
        ...previous,
        date_filter: { relative, timezone_offset: -new Date().getTimezoneOffset() },
      }));
    }
  };

  const handleSortFilterChange = (sortKey: string) => {
    const sortMapping: Record<string, { field: string; direction: "asc" | "desc"; sortKey: SurveySortKey }> = {
      newest_first: { field: "created_at", direction: "desc", sortKey: "newest_first" },
      oldest_first: { field: "created_at", direction: "asc", sortKey: "oldest_first" },
      least_points: { field: "points", direction: "asc", sortKey: "least_points" },
      most_points: { field: "points", direction: "desc", sortKey: "most_points" },
      most_responses: { field: "responses_count", direction: "desc", sortKey: "most_responses" },
    };
    const sortConfig = sortMapping[sortKey];
    if (sortConfig) {
      setSortFilter(sortKey);
      setPage(1);
      setSorting(sortConfig);
    }
  };

  const handleClearDateRange = () => {
    setTimeFilter("All time");
    setStartDate(undefined);
    setEndDate(undefined);
    setFilters(({ date_filter: _dateFilter, ...rest }) => rest);
  };

  const handleDateRangeChange = useCallback(() => {
    if (!startDate || !endDate) return;
    const from = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).toISOString();
    const to = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59, 999).toISOString();
    setFilters((previous) => ({ ...previous, date_filter: { from, to } }));
  }, [endDate, setFilters, startDate]);

  useEffect(() => {
    if (dayjs(startDate).isValid() && dayjs(endDate).isValid() && timeFilter === "Custom Range") {
      handleDateRangeChange();
    }
  }, [endDate, handleDateRangeChange, startDate, timeFilter]);

  const hasActiveFilters = useMemo(
    () => Object.keys(filters).length > 0 || Boolean(search.trim()) || timeFilter !== "All time" || sortFilter !== "newest_first",
    [filters, search, sortFilter, timeFilter],
  );

  const filterState: SurveyPageFilterState = { search, timeFilter, sortFilter, startDate, endDate };
  const filterHandlers: SurveyPageFilterHandlers = {
    onSearchChange: setSearch,
    onTimeFilterChange: handleTimeFilterChange,
    onSortFilterChange: handleSortFilterChange,
    onStartDateChange: setStartDate,
    onEndDateChange: setEndDate,
    onClearDateRange: handleClearDateRange,
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearch("");
    setTimeFilter("All time");
    setSortFilter("newest_first");
    setStartDate(undefined);
    setEndDate(undefined);
    setPage(1);
    setSorting({ field: "created_at", direction: "desc", sortKey: "newest_first" });
  };

  return (
    <>
      <ContentSectionHeader
        title={t("survey.survey_list")}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {hasActiveFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearFilters}
                disabled={isLoading}
                className="flex items-center gap-2"
              >
                <X className="h-4 w-4" />
                {t("common.clear")}
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={() => void onRefresh()}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
              {t("ui.refresh")}
            </Button>
          </div>
        }
      />
      <SurveyPageFilters t={t} filters={filterState} handleChange={filterHandlers} />
    </>
  );
};

export default SurveyListToolbar;
